<template>
  <div class="tags-panel w-full">
    <div class="tags-panel__container">
      <div class="tags-panel__column tags-panel__column--full">
        <div class="flex w-full justify-between items-center">
          <div class="w-full">
            <div class="flex flex-wrap w-full justify-between">
              <div class="tags-panel__action control-label">{{ action }}</div>
              <slot></slot>
            </div>
            <div v-if="selectedTags" class="inline-flex items-baseline flex-wrap tags-panel__tags">
              <template v-for="tag in selectedTags">
                <span :key="tag.id" class="tags-panel__tag">{{ tag.name }}</span>
              </template>
              <div v-if="isUpdatingExistingTag || isMergingExistingTags" class="inline-flex items-center m-l-sm">to:</div>
            </div>
          </div>
        </div>
        <form class="w-full tags-panel__form" @submit.prevent>
          <div class="add-tags-form" :class="$hasErrorClass($v.name, 'form-group')">
            <div class="add-tags-form__column">
              <input name="name" type="text" class="form-control input-sm" placeholder="Tag name" v-model="$v.name.$model"/>
              <vuelidate-help :field="$v.name" label="Name" />
            </div>
            <div class="add-tags-form__column add-tags-form__column--cta m-l-sm">
              <ladda-button
                type="submit"
                class="btn btn-block btn-primary btn-block"
                :click="handleActionButtonCallback">
                {{ action }}
              </ladda-button>
            </div>
          </div>
          <div class="flex">
            <div v-if="hasTagType" class="w-1/2 margin-t-2" :class="$hasErrorClass($v.tagType, 'form-group')">
              <selectize
                class="selectize selectize-single form-control"
                name="tag_type"
                :placeholder="'Tag type'"
                :options="tagTypeOptions"
                v-model="$v.tagType.$model"
                :disabled="this.$route.query.entity === 'interest-categories' && !!this.tagTypeText"
                :disable-delete="true">
              </selectize>
              <vuelidate-help :field="$v.tagType" label="Tag type" />
              <a class="cursor-pointer pull-right help-block" v-if="this.$route.query.entity === 'interest-categories' && this.tagType" @click="() => { $v.tagType.$model = null }">Clear</a>
            </div>
            <div v-if="hasTagType && this.$route.query.entity === 'interest-categories'" class="spacer-x-2" style="margin-top: 28px;"><strong>OR</strong></div>
            <div v-if="hasTagType && this.$route.query.entity === 'interest-categories'" class="w-1/2 margin-t-2" :class="$hasErrorClass($v.tagTypeText, 'form-group')">
              <input
                class="form-control"
                name="tag_type_text"
                placeholder="New tag type..."
                :disabled="this.$route.query.entity === 'interest-categories' && !!this.$v.tagType.$model"
                v-model="$v.tagTypeText.$model"/>
              <vuelidate-help :field="$v.tagTypeText" label="New tag type" />
            </div>
          </div>
          <div class="flex" v-if="showMergeIntoOptions">
            <div class="tags-panel__action control-label">Or merge into</div>
          </div>
          <div class="flex" v-if="showMergeIntoOptions">
            <div class="w-1/2 margin-t-2" :class="$hasErrorClass($v.mergeInto, 'form-group')">
              <selectize
                class="selectize selectize-single form-control"
                name="merge_into"
                :placeholder="'Merge into'"
                :options="mergeIntoOptions"
                v-model="$v.mergeInto.$model"
                :disabled="!(this.$route.query.entity === 'career')"
                :disable-delete="true">
              </selectize>
            </div>
            <div class="margin-t-2 m-l-sm">
              <ladda-button
                type="submit"
                class="btn btn-block btn-primary btn-block"
                :click="handleMergeInto">
                Merge Into
              </ladda-button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { notify } from 'utils'
import { map } from 'lodash'
import Selectize from '../ext/codium/CodiumSelectize'
import { requiredIf } from 'vuelidate/lib/validators'
import VuelidateHelp from '../misc/VuelidateHelp'

export default {
  components: {
    Selectize,
    VuelidateHelp
  },
  props: {
    selectedTags: {
      required: true,
      type: Array
    },
    tagTypeOptions: {
      required: true,
      type: Array
    },
    loading: {
      required: true,
      type: Boolean
    }
  },

  data () {
    return {
      name: null,
      tagType: null,
      tagTypeText: null,
      mergeInto: null
    }
  },

  validations () {
    return {
      name: {
        required: requiredIf(() => {
          if (this.$route.query.entity === 'interest-categories' && this.isUpdatingExistingTag && this.tagType) {
            return false
          }
          return true
        })
      },
      mergeInto: {
        required: false
      },
      tagType: {
        required: requiredIf(() => {
          return ((this.isMergingExistingTags || this.isCreatingNewTag) && this.tagTypeOptions && this.tagTypeOptions.length) && ((!this.tagTypeText && this.$route.query.entity === 'interest-categories') || this.$route.query.entity !== 'interest-categories')
        })
      },
      tagTypeText: {
        required: requiredIf(() => {
          return ((this.isMergingExistingTags || this.isCreatingNewTag) && this.tagTypeOptions && this.tagTypeOptions.length) && (!this.tagType && this.$route.query.entity === 'interest-categories')
        })
      }
    }
  },

  computed: {
    hasTagType () {
      return this.tagTypeOptions && this.tagTypeOptions.length && ((this.isMergingExistingTags || this.isCreatingNewTag) || (this.$route.query.entity === 'interest-categories' && this.isUpdatingExistingTag))
    },

    /**
     * Get total selected tags.
     *
     * @return int
     */
    totalSelectedTags () {
      return this.selectedTags.length
    },
    showMergeIntoOptions () {
      return this.mergeIntoOptions.length > 1
    },
    mergeIntoOptions () {
      return this.selectedTags.map((item) => {
        return {
          id: item.id,
          show: true,
          text: item.name
        }
      })
    },

    /**
     * Determine if we have at least two tags selected.
     *
     * @return bool
     */
    haveAtLeastTwoSelectedTag () {
      return this.totalSelectedTags >= 2
    },

    /**
     * Determine if creating a new tag.
     *
     * @return bool
     */
    isCreatingNewTag () {
      return this.totalSelectedTags === 0
    },

    /**
     * Determine if renaming an existing tag.
     *
     * @return bool
     */
    isUpdatingExistingTag () {
      return this.totalSelectedTags === 1
    },

    /**
     * Determine if merging existing tags.
     *
     * @return bool
     */
    isMergingExistingTags () {
      return this.totalSelectedTags > 1
    },

    /**
     * The action text for both button and leading text on paragraph.
     *
     * @return string
     */
    action () {
      if (this.isCreatingNewTag) {
        return 'Add'
      } else if (this.isUpdatingExistingTag) {
        return 'Update'
      } else if (this.isMergingExistingTags) {
        return 'Merge'
      }
    }
  },

  methods: {
    async handleMergeInto () {
      if (!this.$v.mergeInto) {
        notify.error('Please select a tag to merge into')
        return Promise.resolve()
      }

      const payload = {
        tags: map(this.selectedTags, 'id'),
        id: this.mergeInto
      }
      this.$emit('mergeInto', payload)
    },
    /**
     * Handle the action button being pressed.
     *
     * @return Promise
     */
    async handleActionButtonCallback () {
      if (this.$v.$invalid) {
        this.$v.$touch()
        notify.error('Failed validation, please check fields and try again')
        return Promise.resolve()
      }
      const type = this.tagTypeText ? this.tagTypeText : this.tagType
      if (this.isCreatingNewTag) {
        this.$emit('create', {
          name: this.name,
          type
        })
      } else if (this.isUpdatingExistingTag) {
        const payload = {
          id: this.selectedTags[0].id
        }
        if (this.name) {
          payload.name = this.name
        }
        if (type) {
          payload.group = type
        }
        this.$emit('rename', payload)
      } else if (this.isMergingExistingTags) {
        let payload = {
          ids: map(this.selectedTags, 'id'),
          type: type,
          name: this.name
        }
        this.$emit('merge', payload)
      }

      await setTimeout(() => { return this.loading === true }, 50)

      this.name = ''
      this.tagType = null
      this.tagTypeText = null
      this.$v.$reset()
      return Promise.resolve()
    },

    /**
     * Reset the form and clear selected tags.
     *
     * @return void
     */
    resetFormAndClearSelectedTags () {
      this.$emit('input', [])

      this.name = null

      this.$v.$reset()
    }
  }
}
</script>
